import React from 'react';
import { BarChart3, Webhook, Clock, ArrowRightLeft } from 'lucide-react';

const relTime = (iso) => {
  if (!iso) return 'never';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const Sparkline = ({ series, testid }) => {
  const w = 112, h = 28;
  const max = Math.max(1, ...series);
  const pts = series
    .map((v, i) => `${((i / (series.length - 1)) * w).toFixed(1)},${(h - 1 - (v / max) * (h - 2)).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} data-testid={testid} aria-label="calls per day, last 14 days">
      <polyline points={`0,${h} ${pts} ${w},${h}`} fill="currentColor" className="text-cn-primary/15" stroke="none" />
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-cn-primary" />
    </svg>
  );
};

const VolumeBars = ({ days, production, sandbox }) => {
  const max = Math.max(1, ...days.map((_, i) => production[i] + sandbox[i]));
  return (
    <div className="flex items-end gap-2 h-24" data-testid="usage-referral-chart">
      {days.map((d, i) => (
        <div key={d} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <div className="w-full max-w-[28px] flex flex-col justify-end h-20">
            {production[i] > 0 && (
              <div className="w-full bg-cn-primary rounded-t-sm" style={{ height: `${(production[i] / max) * 100}%` }} title={`${production[i]} production`} />
            )}
            {sandbox[i] > 0 && (
              <div className={`w-full bg-slate-300 dark:bg-cn-ink-600 ${production[i] === 0 ? 'rounded-t-sm' : ''}`} style={{ height: `${(sandbox[i] / max) * 100}%` }} title={`${sandbox[i]} sandbox`} />
            )}
            {production[i] + sandbox[i] === 0 && <div className="w-full h-px bg-slate-200 dark:bg-cn-ink-700" />}
          </div>
          <span className="text-[9px] text-slate-400 truncate">{d.slice(5)}</span>
        </div>
      ))}
    </div>
  );
};

const StatTile = ({ testid, label, value, sub }) => (
  <div data-testid={testid} className="p-4 rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800">
    <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
    <p className="text-2xl font-bold font-display mt-1 text-slate-900 dark:text-white">{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

export const ApiUsageSection = ({ usage }) => {
  if (!usage) return null;
  const t = usage.totals || {};
  const vol = usage.referral_volume || { days: [], production: [], sandbox: [] };
  const keys = (usage.keys || []).filter((k) => !k.revoked);

  return (
    <div data-testid="api-usage-section" className="mb-8">
      <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-cn-primary" /> API usage
        <span className="text-xs font-normal text-slate-500">last 14 days</span>
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <StatTile testid="usage-tile-calls" label="API calls (14d)" value={(t.calls_14d ?? 0).toLocaleString()} />
        <StatTile testid="usage-tile-referrals" label="API referrals (7d)" value={t.referrals_7d ?? 0} sub="production + sandbox" />
        <StatTile
          testid="usage-tile-webhook"
          label="Webhook success rate"
          value={t.webhook_success_rate == null ? '—' : `${t.webhook_success_rate}%`}
          sub={t.webhook_success_rate == null ? 'no deliveries yet' : `${t.webhook_delivered} delivered · ${t.webhook_failed} failed`}
        />
      </div>

      {/* 7-day referral volume */}
      <div className="rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
            <ArrowRightLeft className="w-3.5 h-3.5 text-cn-primary" /> Referral volume — 7 days
          </p>
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-cn-primary inline-block" /> production</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-300 dark:bg-cn-ink-600 inline-block" /> sandbox</span>
          </div>
        </div>
        <VolumeBars days={vol.days} production={vol.production} sandbox={vol.sandbox} />
      </div>

      {/* Per-key sparklines */}
      {keys.length === 0 ? (
        <p className="text-sm text-slate-500 py-4 text-center" data-testid="usage-keys-empty">No active keys to chart yet.</p>
      ) : (
        <div className="space-y-2">
          {keys.map((k) => {
            const prod = k.scope === 'production';
            return (
              <div key={k.id} data-testid={`usage-key-row-${k.id}`}
                className="rounded-xl border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800 p-3 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 dark:text-white text-sm">{k.org_name}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${prod ? 'bg-cn-primary text-white' : 'bg-slate-100 text-slate-600 dark:bg-cn-ink-700 dark:text-slate-300'}`}>
                      {prod ? 'PRODUCTION' : 'sandbox'}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                    <code className="font-mono">{k.prefix}</code>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {relTime(k.last_used_at)}</span>
                    {k.webhook && (
                      <span className="inline-flex items-center gap-1" data-testid={`usage-webhook-rate-${k.id}`}>
                        <Webhook className="w-3 h-3" />
                        {k.webhook.success_rate == null ? 'pending' : `${k.webhook.success_rate}% delivered`}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-900 dark:text-white" data-testid={`usage-calls-14d-${k.id}`}>{k.calls_14d.toLocaleString()}</p>
                    <p className="text-[10px] text-slate-400">calls · 14d</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{k.referrals_7d}</p>
                    <p className="text-[10px] text-slate-400">referrals · 7d</p>
                  </div>
                  <Sparkline series={k.series} testid={`usage-sparkline-${k.id}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApiUsageSection;
